import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useLocalAuth } from '../../hooks/useLocalAuth';
import { AuthLayout } from './AuthLayout';
import { AlertCircle, Shield, User, Building2, Check, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { OrganizationPlansDialog } from '../OrganizationPlansDialog';

const registerSchema = z.object({
  name: z.string().min(2, 'Name muss mindestens 2 Zeichen lang sein'),
  email: z.string().email('Ungültige E-Mail-Adresse'),
  password: z.string()
    .min(8, 'Passwort muss mindestens 8 Zeichen lang sein')
    .regex(/[A-Z]/, 'Passwort muss mindestens einen Großbuchstaben enthalten')
    .regex(/[a-z]/, 'Passwort muss mindestens einen Kleinbuchstaben enthalten')
    .regex(/[0-9]/, 'Passwort muss mindestens eine Zahl enthalten'),
  organizationName: z.string().min(2, 'Organisationsname muss mindestens 2 Zeichen lang sein'),
  organizationPlan: z.enum(['free', 'starter', 'professional', 'enterprise']),
  company: z.string().optional(),
  role: z.enum(['admin', 'technician']),
  adminCode: z.string().optional(),
});

type RegisterFormData = z.infer<typeof registerSchema>;

// Der Administrator-Code ist hier klar definiert und sichtbar
const ADMIN_CODE = 'ADMIN2025'; // In einer echten Anwendung würde dieser sicher gespeichert werden

export const RegisterForm: React.FC = () => {
  const { register: registerUser, loading } = useLocalAuth();
  const [error, setError] = useState<string | null>(null);
  const [selectedRole, setSelectedRole] = useState<'admin' | 'technician'>('admin');
  const [adminCode, setAdminCode] = useState('');
  const [adminCodeError, setAdminCodeError] = useState<string | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<'free' | 'starter' | 'professional' | 'enterprise'>('free');
  const [showPlansDialog, setShowPlansDialog] = useState(false);
  const { register, handleSubmit, formState: { errors, isSubmitting }, setValue } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      role: 'admin',
      organizationPlan: 'free'
    }
  });
  const navigate = useNavigate();

  const onSubmit = async (data: RegisterFormData) => {
    try {
      setError(null);
      setAdminCodeError(null);

      // Validate admin code if registering as admin
      if (data.role === 'admin' && adminCode !== ADMIN_CODE) {
        setAdminCodeError(`Ungültiger Administrator-Code. Der korrekte Code lautet: ${ADMIN_CODE}`);
        return;
      }

      await registerUser({
        ...data,
        role: data.role,
        organizationName: data.organizationName,
        organizationPlan: selectedPlan
      });
      navigate('/dashboard');
    } catch (error) {
      if (error instanceof Error) {
        setError(error.message);
      } else {
        setError('Ein unerwarteter Fehler ist aufgetreten');
      }
    }
  };

  const handlePlanSelection = (plan: string) => {
    setSelectedPlan(plan as 'free' | 'starter' | 'professional' | 'enterprise');
    setValue('organizationPlan', plan as 'free' | 'starter' | 'professional' | 'enterprise');
  };

  const getPlanLabel = (plan: typeof selectedPlan) => {
    switch (plan) {
      case 'free': return 'Kostenlos';
      case 'starter': return 'Starter';
      case 'professional': return 'Professional';
      case 'enterprise': return 'Enterprise';
    }
  };

  const getPlanFeatures = (plan: typeof selectedPlan) => {
    switch (plan) {
      case 'free': return ['1 Benutzer', '3 Gebäude', 'Basis-Funktionen'];
      case 'starter': return ['5 Benutzer', '10 Gebäude', 'API-Zugriff'];
      case 'professional': return ['20 Benutzer', 'Unbegrenzt Gebäude', 'Power BI'];
      case 'enterprise': return ['Unbegrenzt Benutzer', 'Vollständig anpassbar', '24/7 Support'];
    }
  };

  return (
    <AuthLayout
      title="Organisation erstellen"
      subtitle="Erstellen Sie Ihre eigene Organisation"
      altLink={{
        text: "Bereits registriert?",
        label: "Anmelden",
        to: "/login"
      }}
    >
      <div className="bg-white p-6 rounded-lg shadow-sm">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {error && (
            <div className="rounded-md bg-red-50 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <AlertCircle className="h-5 w-5 text-red-400" />
                </div>
                <div className="ml-3">
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* Organization Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
              <Building2 className="w-5 h-5 text-blue-600" />
              Organisation
            </h3>
            
            <div>
              <label htmlFor="organizationName" className="block text-sm font-medium text-gray-700">
                Organisationsname
              </label>
              <div className="mt-1">
                <input
                  {...register('organizationName')}
                  type="text"
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Name Ihrer Organisation"
                />
                {errors.organizationName && (
                  <p className="mt-1 text-sm text-red-600">{errors.organizationName.message}</p>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Plan auswählen
              </label>
              <button
                type="button"
                onClick={() => setShowPlansDialog(true)}
                className="w-full bg-white border border-gray-300 rounded-lg p-4 text-left hover:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <div className="flex justify-between items-center">
                  <div>
                    <h4 className="font-medium text-gray-900">{getPlanLabel(selectedPlan)}</h4>
                    <ul className="mt-2 space-y-1">
                      {getPlanFeatures(selectedPlan).map((feature, index) => (
                        <li key={index} className="text-sm text-gray-600 flex items-center gap-1">
                          <Check className="w-4 h-4 text-blue-500" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                </div>
              </button>
              <input
                type="hidden"
                {...register('organizationPlan')}
                value={selectedPlan}
              />
            </div>
          </div>

          {/* Administrator Section */}
          <div className="space-y-4 pt-6 border-t">
            <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
              <User className="w-5 h-5 text-blue-600" />
              Administrator
            </h3>

            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                Name
              </label>
              <div className="mt-1">
                <input
                  {...register('name')}
                  type="text"
                  autoComplete="name"
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
                )}
              </div>
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                E-Mail
              </label>
              <div className="mt-1">
                <input
                  {...register('email')}
                  type="email"
                  autoComplete="email"
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
                )}
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Passwort
              </label>
              <div className="mt-1">
                <input
                  {...register('password')}
                  type="password"
                  autoComplete="new-password"
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
                {errors.password && (
                  <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
                )}
                <div className="mt-1 text-xs text-gray-500 space-y-1">
                  <p className="flex items-center gap-1">
                    <Shield className="w-3 h-3 text-blue-500" />
                    Passwort muss mindestens 8 Zeichen lang sein
                  </p>
                  <p className="flex items-center gap-1">
                    <Shield className="w-3 h-3 text-blue-500" />
                    Passwort muss mindestens einen Großbuchstaben enthalten
                  </p>
                  <p className="flex items-center gap-1">
                    <Shield className="w-3 h-3 text-blue-500" />
                    Passwort muss mindestens einen Kleinbuchstaben enthalten
                  </p>
                  <p className="flex items-center gap-1">
                    <Shield className="w-3 h-3 text-blue-500" />
                    Passwort muss mindestens eine Zahl enthalten
                  </p>
                  <p className="flex items-center gap-1">
                    <Shield className="w-3 h-3 text-blue-500" />
                    Passwort wird auf Sicherheit geprüft (keine bekannten kompromittierten Passwörter)
                  </p>
                </div>
              </div>
            </div>

            <div>
              <label htmlFor="company" className="block text-sm font-medium text-gray-700">
                Firma (optional)
              </label>
              <div className="mt-1">
                <input
                  {...register('company')}
                  type="text"
                  autoComplete="organization"
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
                {errors.company && (
                  <p className="mt-1 text-sm text-red-600">{errors.company.message}</p>
                )}
              </div>
            </div>

            <div>
              <label htmlFor="adminCode" className="block text-sm font-medium text-gray-700">
                Administrator-Code
              </label>
              <div className="mt-1">
                <input
                  type="password"
                  value={adminCode}
                  onChange={(e) => setAdminCode(e.target.value)}
                  className={`appearance-none block w-full px-3 py-2 border ${adminCodeError ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500`}
                  placeholder="Geben Sie den Administrator-Code ein"
                />
                {adminCodeError && (
                  <p className="mt-1 text-sm text-red-600">{adminCodeError}</p>
                )}
                <p className="mt-1 text-sm text-gray-500">
                  Hinweis: Der Administrator-Code lautet <strong>ADMIN2025</strong>
                </p>
              </div>
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={isSubmitting || loading}
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting || loading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                'Organisation erstellen'
              )}
            </button>
          </div>
        </form>
      </div>

      {showPlansDialog && (
        <OrganizationPlansDialog
          onClose={() => setShowPlansDialog(false)}
          onSelectPlan={handlePlanSelection}
        />
      )}
    </AuthLayout>
  );
};